// =============================================================================
// Activity feed — chronological list of every recent change/comment across
// the events the user can see. Rendered when the bottom toggle is set to
// "Activity". Calendar.js builds the feed; tapping an event opens its
// detail view there.
// =============================================================================

import React from 'react';
import { Bell, MessageSquare, Users as UsersIcon } from 'lucide-react';
import { format, parseISO } from 'date-fns';
import { formatDate as fmt } from '../../lib/dates';
import { useTranslation } from '../../lib/i18n';

const ActivityFeed = ({ feed, getCategory, renderCreatorAvatar, onOpenEvent }) => {
  const { t } = useTranslation();
  return (
    <div className="space-y-3 pb-24">
      <h2 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100">
        {t('calendar.activity')}
      </h2>
      {feed.length === 0 ? (
        <div className="text-center py-16 text-gray-500 dark:text-gray-400">
          <Bell size={32} className="mx-auto mb-3 opacity-50" />
          <p className="text-sm">{t('calendar.noActivity')}</p>
          <p className="text-xs mt-1 opacity-70">
            {t('calendar.noActivityHint')}
          </p>
        </div>
      ) : (
        <div className="space-y-3">
          {feed.map(({ event: ev, sharedCalendar, entries }) => {
            const cat = getCategory(ev.categoryId);
            const color = (cat && cat.color) || '#6B7280';
            const fieldLabelDe = (f) => {
              switch (f) {
                case 'title': return t('common.title');
                case 'date': return t('common.date');
                case 'label': return t('calendar.fieldLabel');
                case 'notes': return t('common.notes');
                case 'participants': return t('sharedCalendar.participants');
                case 'recurrence': return t('calendar.recurrence');
                default: return f;
              }
            };
            const auditSummaryDe = (a) => {
              if (a.type === 'created') return t('calendar.auditCreated');
              const fields = a.fields || [];
              if (fields.length === 0) return t('calendar.auditUpdated');
              return t('calendar.auditFieldsUpdated', { fields: fields.map(fieldLabelDe).join(', ') });
            };
            const fmtRel = (iso) => {
              if (!iso) return '';
              try {
                const d = parseISO(iso);
                const today = new Date();
                const diffDays = Math.floor(
                  (new Date(today).setHours(0, 0, 0, 0) -
                    new Date(d).setHours(0, 0, 0, 0)) /
                    86400000
                );
                if (diffDays === 0)
                  return `${t('common.today', 'Heute')}, ${format(d, 'HH:mm')}`;
                if (diffDays === 1)
                  return `${t('common.yesterday', 'Gestern')}, ${format(d, 'HH:mm')}`;
                return fmt(d, 'shortTime');
              } catch {
                return iso;
              }
            };
            const fmtEventTime = () => {
              try {
                const d = parseISO(ev.date);
                const dateStr = fmt(d, 'weekdayShort');
                if (ev.allDay === false && ev.time) {
                  const end = ev.endTime ? `–${ev.endTime}` : '';
                  return `${dateStr}, ${ev.time}${end}`;
                }
                return dateStr;
              } catch {
                return ev.date || '';
              }
            };
            const participantIds =
              Array.isArray(ev.participantIds) && ev.participantIds.length > 0
                ? ev.participantIds
                : ev.createdBy
                ? [ev.createdBy]
                : [];
            return (
              <div
                key={ev.id}
                className="bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-800 rounded-xl overflow-hidden"
              >
                <button
                  type="button"
                  onClick={() => onOpenEvent(ev)}
                  className="w-full flex items-start gap-3 p-3 sm:p-4 text-left hover:bg-gray-50 dark:hover:bg-gray-800/40 transition-colors"
                >
                  <div
                    className="w-1 self-stretch rounded-full flex-shrink-0"
                    style={{ backgroundColor: color }}
                  />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold text-gray-900 dark:text-gray-100 truncate">
                      {ev.title || t('calendar.eventFallback')}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5 flex-wrap">
                      {sharedCalendar && (
                        <>
                          <UsersIcon size={11} />
                          <span className="truncate">{sharedCalendar.name}</span>
                          <span className="opacity-50">·</span>
                        </>
                      )}
                      <span>{fmtEventTime()}</span>
                    </div>
                  </div>
                  {participantIds.length > 0 && (
                    <div className="flex items-center -space-x-2 flex-shrink-0">
                      {participantIds.slice(0, 3).map((uid) => (
                        <div
                          key={uid}
                          className="ring-2 ring-white dark:ring-gray-900 rounded-full"
                        >
                          {renderCreatorAvatar(uid, 24)}
                        </div>
                      ))}
                    </div>
                  )}
                </button>
                <div className="border-t border-gray-100 dark:border-gray-800 divide-y divide-gray-100 dark:divide-gray-800">
                  {entries.slice(0, 5).map((item) => (
                    <div
                      key={item.id}
                      className="flex items-center gap-2 px-3 sm:px-4 py-2 text-xs"
                    >
                      {renderCreatorAvatar(item.userId, 20)}
                      {item.kind === 'audit' ? (
                        <span className="text-gray-700 dark:text-gray-300 truncate flex-1">
                          {auditSummaryDe(item)}
                        </span>
                      ) : (
                        <span className="text-gray-700 dark:text-gray-300 truncate flex-1 flex items-center gap-1.5">
                          <MessageSquare size={11} className="opacity-60 flex-shrink-0" />
                          <span className="truncate">{item.text}</span>
                        </span>
                      )}
                      <span className="text-gray-400 dark:text-gray-500 flex-shrink-0 whitespace-nowrap">
                        {fmtRel(item.at)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
